import {
    useEffect,
    useState,
    Fragment,
    type FC,
    useRef
} from "react";
import {
    type PointerEvent,
    View
} from "react-native";
import type INoticeProps from "./type";
import type {
    NoticeType
} from "./type";
import stylesheet, {
    getNoticeAutoCloseDelay,
    getNoticeActionRadius,
    getNoticeIconColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    type NCoreUIKitIcon
} from "../../types";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    TriangleAlert as TriangleAlertIcon,
    CircleCheck as CircleCheckIcon,
    CircleAlert as CircleAlertIcon,
    Sparkles as SparklesIcon,
    Info as InfoIcon,
    type LucideIcon,
    X as XIcon
} from "lucide-react-native";
import AnimatedIcon from "../animatedIcon";
import Loading from "../loading";
import Button from "../button";
import Text from "../text";

const NoticeTypeIcon: Partial<Record<NoticeType, LucideIcon>> = {
    warning: TriangleAlertIcon,
    success: CircleCheckIcon,
    danger: CircleAlertIcon,
    primary: SparklesIcon,
    info: InfoIcon
};

const NoticeCloseIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    icon={XIcon}
    size={size}
/>;

const Notice: FC<INoticeProps> = ({
    onPointerEnter: onPointerEnterProp,
    onPointerLeave: onPointerLeaveProp,
    isLiveRegionEnabled = true,
    "aria-live": ariaLiveProp,
    isAutoClosePaused = false,
    contentContainerStyle,
    isShowClose = false,
    isCompact = false,
    isLoading = false,
    type = "neutral",
    secondaryAction,
    autoCloseDelay,
    customLocalize,
    icon: IconProp,
    role: roleProp,
    onClosePress,
    customTheme,
    onAutoClose,
    children,
    onPress,
    action,
    style,
    title,
    body,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const [
        focusCount,
        setFocusCount
    ] = useState(0);

    const onAutoCloseRef = useRef(onAutoClose);

    const remainingTime = useRef(0);

    const isActionVisible = !!action || !!secondaryAction || isShowClose;

    const isAssertive = type === "danger";

    const isAutoCloseEnabled = !!onAutoClose;

    const isAutoCloseStopped = isAutoClosePaused || isLoading || isHovered || focusCount > 0;

    const currentAutoCloseDelay = autoCloseDelay ?? getNoticeAutoCloseDelay({
        durations,
        title,
        body
    });

    const currentIconColors = getNoticeIconColors({
        colors,
        type
    });

    const actionRadius = getNoticeActionRadius({
        controlHeights,
        typography,
        isCompact,
        radiuses,
        borders,
        spaces
    });

    const {
        actionsContainer: actionsContainerDynamicStyle,
        contentContainer: contentContainerDynamicStyle,
        iconContainer: iconContainerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        isCustomContent: !!children,
        controlPaddings,
        isActionVisible,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        shadowAlpha,
        elevations,
        typography,
        iconSizes,
        isCompact,
        radiuses,
        borders,
        colors,
        spaces
    });

    useEffect(() => {
        onAutoCloseRef.current = onAutoClose;
    });

    useEffect(() => {
        remainingTime.current = currentAutoCloseDelay;
    }, [
        currentAutoCloseDelay
    ]);

    useEffect(() => {
        if(!isAutoCloseEnabled || isAutoCloseStopped) {
            return;
        }

        const startedAt = Date.now();

        const timeout = setTimeout(() => {
            onAutoCloseRef.current?.();
        }, remainingTime.current);

        return () => {
            clearTimeout(timeout);

            remainingTime.current = Math.max(0, remainingTime.current - (Date.now() - startedAt));
        };
    }, [
        currentAutoCloseDelay,
        isAutoCloseStopped,
        isAutoCloseEnabled
    ]);

    const onPointerEnter = (event: PointerEvent) => {
        setIsHovered(true);

        onPointerEnterProp?.(event);
    };

    const onPointerLeave = (event: PointerEvent) => {
        setIsHovered(false);

        onPointerLeaveProp?.(event);
    };

    const onActionFocus = () => {
        setFocusCount((count) => count + 1);
    };

    const onActionBlur = () => {
        setFocusCount((count) => Math.max(0, count - 1));
    };

    const renderIcon = () => {
        if(isLoading) {
            return <View
                style={[
                    stylesheet.iconContainer,
                    iconContainerDynamicStyle
                ]}
            >
                <Loading
                    color={currentIconColors.iconColorKey}
                    customTheme={customTheme}
                    size={iconSizes.small}
                />
            </View>;
        }

        if(IconProp) {
            return <View
                style={[
                    stylesheet.iconContainer,
                    iconContainerDynamicStyle
                ]}
                aria-hidden={true}
            >
                <IconProp
                    customColor={currentIconColors.iconColor}
                    color={currentIconColors.iconColorKey}
                    size={iconSizes.small}
                />
            </View>;
        }

        const TypeIcon = NoticeTypeIcon[type];

        if(!TypeIcon) {
            return null;
        }

        return <View
            style={[
                stylesheet.iconContainer,
                iconContainerDynamicStyle
            ]}
            aria-hidden={true}
        >
            <AnimatedIcon
                customColor={currentIconColors.iconColor}
                customTheme={customTheme}
                animationTrigger="mount"
                size={iconSizes.small}
                animation="draw"
                icon={TypeIcon}
            />
        </View>;
    };

    const renderAction = () => {
        if(!action) {
            return null;
        }

        return <Button
            borderRadius={isCompact || !isShowClose ? actionRadius : undefined}
            variant={secondaryAction ? "filled" : "ghost"}
            type={secondaryAction ? "primary" : "neutral"}
            customTheme={customTheme}
            onPress={action.onPress}
            onFocus={onActionFocus}
            spreadBehaviour="free"
            onBlur={onActionBlur}
            title={action.title}
            style={action.style}
            icon={action.icon}
            size="small"
        />;
    };

    const renderSecondaryAction = () => {
        if(!secondaryAction) {
            return null;
        }

        return <Button
            borderRadius={isCompact || !isShowClose ? actionRadius : undefined}
            onPress={secondaryAction.onPress}
            style={secondaryAction.style}
            title={secondaryAction.title}
            icon={secondaryAction.icon}
            customTheme={customTheme}
            onFocus={onActionFocus}
            spreadBehaviour="free"
            onBlur={onActionBlur}
            variant="outline"
            type="neutral"
            size="small"
        />;
    };

    const renderClose = () => {
        if(!isShowClose) {
            return null;
        }

        return <Button
            accessibilityLabel={localize("close")}
            borderRadius={actionRadius}
            customTheme={customTheme}
            onFocus={onActionFocus}
            icon={NoticeCloseIcon}
            spreadBehaviour="free"
            onPress={() => {
                onClosePress?.();
            }}
            onBlur={onActionBlur}
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderActions = () => {
        if(!isActionVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.actionsContainer,
                actionsContainerDynamicStyle
            ]}
        >
            {renderSecondaryAction()}
            {renderAction()}
            {renderClose()}
        </View>;
    };

    const renderContent = () => {
        return <Fragment>
            {renderIcon()}
            <View
                style={[
                    contentContainerStyle,
                    stylesheet.contentContainer,
                    contentContainerDynamicStyle
                ]}
            >
                <Text
                    customTheme={customTheme}
                    variant="titleSmallSize"
                    numberOfLines={3}
                    color="high"
                >
                    {title}
                </Text>
                {
                    body ?
                        <Text
                            customTheme={customTheme}
                            variant="bodyMediumSize"
                            numberOfLines={2}
                            color="mid"
                        >
                            {body}
                        </Text>
                    :
                        null
                }
            </View>
            {renderActions()}
        </Fragment>;
    };

    const liveRegionRole = isAssertive ? "alert" : "status";

    const liveRegionPoliteness = isAssertive ? "assertive" : "polite";

    return <View
        {...props}
        aria-live={ariaLiveProp ?? (isLiveRegionEnabled ? liveRegionPoliteness : undefined)}
        role={roleProp ?? (isLiveRegionEnabled ? liveRegionRole : undefined)}
        onStartShouldSetResponder={onPress ? () => true : undefined}
        onResponderRelease={onPress ? () => onPress() : undefined}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        style={[
            style,
            stylesheet.surface,
            surfaceDynamicStyle
        ]}
    >
        {children ? children : renderContent()}
    </View>;
};
export default Notice;
